/* -------------------------------------------------------------------------- */
/*                    Componente SlidingPaneAddEditCustomer                   */
/* -------------------------------------------------------------------------- */
// Este componente permite crear o editar clientes

import * as authSelectors from '@redux/reducers/auth';
import omit from 'lodash/omit';
import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import SlidingPane from 'react-sliding-pane';
import { Button, Col, Row } from 'reactstrap';
import { v4 as uuidv4 } from 'uuid';
import Membership from 'views/formComponents/Membership';
import * as customerActions from 'views/screens/Customers/actions';
import * as customerSelectors from 'views/screens/Customers/reducers';
import TextInput from '../../formComponents/TextInput';
import LocationsDropdownInput from '../LocationDropDown';

interface Props {
  customer: any;
  isOpenSlidingPane: any;
  closeSlidingPaneAddEditCustomer: any;
  enableSelectLocation?: boolean;
}

const SlidingPaneAddEditCustomer = (props: Props) => {
  const {
    customer,
    isOpenSlidingPane,
    closeSlidingPaneAddEditCustomer,
    enableSelectLocation = false
  } = props;
  const { control, handleSubmit, watch, reset } = useForm();
  const intl = useIntl();
  const dispatch = useDispatch();
  const initializedCustomerIdRef = useRef<string | null>(null);

  const formValues = watch();

  const { isNew, initialValues, parsers } = useSelector((state) => {
    const initialValues = customerSelectors.getSelectedCustomer(state);
    return {
      isNew: initialValues ? false : true,
      initialValues,
      parsers: authSelectors.getParsers(state)
    };
  });

  const editCustomer = (values) => {
    dispatch(customerActions.startEditingCustomer(getValues(initialValues?.customerId, values)));
    closeSlidingPaneAddEditCustomer();
    reset();
  };

  const createCustomer = (values) => {
    dispatch(customerActions.startAddingCustomer(getValues(uuidv4(), values)));
    closeSlidingPaneAddEditCustomer();
    reset();
  };

  const getValues = (customerId, values) => ({
    ...omit(values, ['membership', 'office', 'officeId']),
    customerId,
    organizationId: values.membership?.organizationId,
    divisionId: values.membership?.divisionId,
    subdivisionId: values.membership?.subdivisionId,
    mainOfficeId: values.mainOfficeId
  });

  useEffect(() => {
    if (customer?.customerId) {
      dispatch(customerActions.selectCustomer(customer?.customerId));
      return () => {
        dispatch(customerActions.deselectCustomer(customer?.customerId));
      };
    }
  }, [customer?.customerId]);

  // Resetear el formulario cuando initialValues cambie
  useEffect(() => {
    // Solo resetear si el customerId cambió o si estamos abriendo el panel
    if (isOpenSlidingPane && initialValues && customer?.customerId && customer?.customerId !== initializedCustomerIdRef.current) {
      initializedCustomerIdRef.current = customer?.customerId || null;
      reset({
        customerName: initialValues?.customerName,
        distributionChannel: initialValues?.distributionChannel,
        businessName: initialValues?.businessName,
        taxId: initialValues?.taxId,
        privateCode: initialValues?.privateCode,
        customerAddress: initialValues?.customerAddress,
        customerEmails: initialValues?.customerEmails,
        contactName1: initialValues?.contactName1,
        contactName2: initialValues?.contactName2,
        contactName3: initialValues?.contactName3,
        phone1: initialValues?.phone1,
        phone2: initialValues?.phone2,
        phone3: initialValues?.phone3,
        comments: initialValues?.comments,
        mainOfficeId: initialValues?.mainOfficeId,
        membership: {
          organizationId: initialValues?.organizationId,
          divisionId: initialValues?.divisionId,
          subdivisionId: initialValues?.subdivisionId
        }
      });
    } else if (isOpenSlidingPane && !customer?.customerId && initializedCustomerIdRef.current !== 'new') {
      // Resetear a valores vacíos cuando es nuevo cliente
      initializedCustomerIdRef.current = 'new';
      reset({});
    } else if (!isOpenSlidingPane) {
      // Limpiar la referencia cuando se cierra el panel
      initializedCustomerIdRef.current = null;
    }
  }, [isOpenSlidingPane, initialValues, customer?.customerId]);

  return (
    <SlidingPane
      className="custom-sliding-pane"
      overlayClassName="custom-overlay-sliding-pane"
      isOpen={isOpenSlidingPane}
      title={intl.formatMessage({ id: isNew ? 'customers.create' : 'customers.edit' })}
      subtitle={isNew ? undefined : initialValues?.customerName}
      width={'45rem'}
      onRequestClose={() => {
        closeSlidingPaneAddEditCustomer();
        reset();
      }}
    >
      <Row>
        <Membership
          name="membership"
          control={control}
          defaultValue={{
            organizationId: initialValues?.organizationId,
            divisionId: initialValues?.divisionId,
            subdivisionId: initialValues?.subdivisionId
          }}
          options={{
            useOperator: false,
            useUserOrganization: true,
            showAllDivision: true,
            showAllSubdivision: true,
            colSize: 6
          }}
        />
        <Col sm="12">
          <Controller
            control={control}
            name={'mainOfficeId'}
            defaultValue={initialValues?.mainOfficeId}
            render={({ field, fieldState }) => (
              <LocationsDropdownInput
                field={field}
                fieldstate={fieldState}
                isInModal
                isClearable
                customerId={initialValues?.customerId}
                id={'mainOfficeInput'}
                locationsFilters={formValues?.membership}
                label={intl.formatMessage({ id: 'customers.centralOffice' })}
              />
            )}
          />
        </Col>
        <Col sm="12">
          <Controller
            control={control}
            name="customerName"
            defaultValue={initialValues?.customerName}
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id={'customerNameInput'}
                label={intl.formatMessage({
                  id: 'customers.customerName'
                })}
                required
              />
            )}
          />
        </Col>
        <Col sm="12">
          <Controller
            control={control}
            name="distributionChannel"
            defaultValue={initialValues?.distributionChannel}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id={'distributionChannelInput'}
                label={intl.formatMessage({
                  id: 'customers.distributionChannel'
                })}
              />
            )}
          />
        </Col>
        <Col sm="12">
          <Controller
            control={control}
            name="businessName"
            defaultValue={initialValues?.businessName}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id={'businessNameInput'}
                label={intl.formatMessage({
                  id: 'customers.businessName'
                })}
              />
            )}
          />
        </Col>
        <Col sm="6">
          <Controller
            control={control}
            name="taxId"
            defaultValue={initialValues?.taxId}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id={'taxIdInput'}
                label={intl.formatMessage({
                  id: 'customers.taxesId'
                })}
              />
            )}
          />
        </Col>
        <Col sm="6">
          <Controller
            control={control}
            name="privateCode"
            defaultValue={initialValues?.privateCode}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id={'privateCodeInput'}
                label={intl.formatMessage({
                  id: 'customers.privateCode'
                })}
              />
            )}
          />
        </Col>
        <Col sm="12">
          <Controller
            control={control}
            name="customerAddress"
            defaultValue={initialValues?.customerAddress}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id={'customerAddressInput'}
                label={intl.formatMessage({
                  id: 'customers.address'
                })}
              />
            )}
          />
        </Col>
        <Col sm="12">
          <Controller
            control={control}
            name="customerEmails"
            defaultValue={initialValues?.customerEmails}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id={'customerEmailsInput'}
                label={intl.formatMessage({
                  id: 'customers.email'
                })}
                type={'email'}
              />
            )}
          />
        </Col>
        <Col sm="6">
          <Controller
            control={control}
            name="contactName1"
            defaultValue={initialValues?.contactName1}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id={'contactName1Input'}
                label={intl.formatMessage({
                  id: 'customers.contact1'
                })}
              />
            )}
          />
        </Col>
        <Col sm="6">
          <Controller
            control={control}
            name="phone1"
            defaultValue={initialValues?.phone1}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id={'phone1Input'}
                label={intl.formatMessage({
                  id: 'customers.phone1'
                })}
              />
            )}
          />
        </Col>
        <Col sm="6">
          <Controller
            control={control}
            name="contactName2"
            defaultValue={initialValues?.contactName2}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id={'contactName2Input'}
                label={intl.formatMessage({
                  id: 'customers.contact2'
                })}
              />
            )}
          />
        </Col>
        <Col sm="6">
          <Controller
            control={control}
            name="phone2"
            defaultValue={initialValues?.phone2}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id={'phone2Input'}
                label={intl.formatMessage({
                  id: 'customers.phone2'
                })}
              />
            )}
          />
        </Col>
        <Col sm="6">
          <Controller
            control={control}
            name="contactName3"
            defaultValue={initialValues?.contactName3}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id={'contactName3Input'}
                label={intl.formatMessage({
                  id: 'customers.contact3'
                })}
              />
            )}
          />
        </Col>
        <Col sm="6">
          <Controller
            control={control}
            name="phone3"
            defaultValue={initialValues?.phone3}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id={'phone3Input'}
                label={intl.formatMessage({
                  id: 'customers.phone3'
                })}
              />
            )}
          />
        </Col>
        <Col sm="12">
          <Controller
            control={control}
            name="comments"
            defaultValue={initialValues?.comments}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id={'commentsInput'}
                label={intl.formatMessage({
                  id: 'customers.comments'
                })}
                type={'textarea'}
              />
            )}
          />
        </Col>
      </Row>

      <br />
      <br />
      <Row className={'footer-slide-pane'}>
        <Col sm="12">
          <Button.Ripple
            className="mr-1 mt-50 mb-50"
            id="saveCustomersButton"
            color="primary"
            /*type="submit"*/
            onClick={handleSubmit((values) =>
              isNew ? createCustomer(values) : editCustomer(values)
            )}
          >
            <FormattedMessage id="common.save" />
          </Button.Ripple>
          <Button.Ripple
            className="mt-50 mb-50"
            color="light"
            onClick={() => {
              closeSlidingPaneAddEditCustomer();
              reset();
            }}
          >
            <FormattedMessage id="common.cancel" />
          </Button.Ripple>
        </Col>
      </Row>
    </SlidingPane>
  );
};

SlidingPaneAddEditCustomer.defaultProps = {};

export default SlidingPaneAddEditCustomer;
